/**
 * The customer page's "Uninvoiced work" section: every billable time entry and parts charge no
 * invoice has taken yet, split into what the next invoice would bill (finished tickets) and what is
 * waiting on unfinished ones - and the customer's monthly auto-billing switch.
 *
 *   * THE LIST IS THE GENERATOR'S RULE, not a second one: billable, on no invoice, time over 0
 *     minutes, priced the way the invoice prices it (`uninvoicedItems` in billing.ts, under test).
 *   * AUTO-BILLING IS ADMIN-ONLY server-side, so a technician sees the controls disabled with a
 *     sentence rather than a Save that answers 403.
 */
import * as auth from '../../api/auth.js';
import * as crm from '../../api/crm.js';
import { describeError } from '../../api/http.js';
import { AutoBillingLimits, type AutoBillingDto } from '../../contracts.gen.js';
import { autoBillLine, uninvoicedItems, uninvoicedTotals, type UninvoicedItem } from '../../billing.js';
import { join, localDate, localDateTime, money, plural } from '../../format.js';
import { Card } from '../../ui/Card.js';
import { CardList } from '../../ui/CardList.js';
import { chip } from '../../ui/Chip.js';
import { confirm } from '../../ui/dialogs.js';
import { EmptyState } from '../../ui/EmptyState.js';
import { StatusLine } from '../../ui/StatusLine.js';
import {
  button,
  buttonRow,
  card,
  checkLine,
  field,
  fillWith,
  FONT,
  GAP,
  h2,
  inlineRow,
  muted,
  relayout,
  splitRow,
  stackOf,
  textBox,
  value,
  vstack,
  wrappingCheck
} from '../../ui/layout.js';
import type { PageContext } from '../page.js';

export interface UninvoicedSection {
  readonly panel: import('webwin').StackPanel;
  refresh(): Promise<void>;
}

export function uninvoicedSection(options: {
  ctx: PageContext;
  customerId: number;
  /** After Generate or Run now, so the page's invoice history shows the new invoice. */
  onInvoiced: () => void;
}): UninvoicedSection {
  const { ctx, customerId } = options;
  const isAdmin = auth.isAdmin();

  const summary = muted('');
  const status = new StatusLine();
  const empty = new EmptyState('Nothing uninvoiced. Billable time and parts logged on this customer\'s tickets appear here until an invoice takes them.');
  const list = new CardList<UninvoicedItem>({
    key: i => i.key,
    version: i => JSON.stringify(i),
    build: buildRow,
    max: 250
  });

  const generate = button('Generate invoice now', () => void generateNow());
  const header = splitRow({ align: 'center' });
  fillWith(header, h2('Uninvoiced work'));
  header.controls.add(generate);

  // ---- Auto-billing ----

  const enabled = wrappingCheck('Automatically bill resolved tickets each month');
  const day = textBox({ width: 70, hAlign: 'start', text: '1' });
  const save = button('Save', () => void saveAutoBilling(), true);
  const runNow = button('Run now', () => void runAutoBillingNow());
  const scheduleLine = muted('');
  const lastLine = muted('');
  const autoStatus = new StatusLine();
  const autoCard = card();
  autoCard.controls.add(
    autoStatus.addTo(
      stackOf(
        vstack({ spacing: GAP.sm }),
        value('Monthly auto-billing', { font: FONT.bold }),
        checkLine(enabled),
        field(`Day of the month (${AutoBillingLimits.MinDay}-${AutoBillingLimits.MaxDay})`, day),
        muted(
          'On that day the work on resolved and closed tickets is invoiced, the invoice is issued and emailed to ' +
          'the billing contact. When nothing is billable no invoice is made. The administrators are emailed either way.'
        ),
        scheduleLine,
        lastLine,
        stackOf(buttonRow({ spacing: GAP.sm }), save, runNow),
        ...(isAdmin ? [] : [muted('Only an administrator can change auto-billing.', 'warn')])
      )
    )
  );
  for (const c of [enabled, day, save, runNow]) c.enabled = isAdmin;
  enabled.on('change', () => (day.enabled = isAdmin));

  const panel = stackOf(
    vstack({ spacing: GAP.sm }),
    header,
    summary,
    status.label,
    empty.panel,
    list.panel,
    autoCard
  );

  function buildRow(i: UninvoicedItem): Card {
    const heading = inlineRow({ spacing: GAP.sm });
    heading.controls.add(value(i.description, { font: FONT.bold }));
    heading.controls.add(chip(i.kind === 'time' ? 'time' : 'part', 'neutral'));
    heading.controls.add(i.ready ? chip('ready to invoice', 'ok') : chip('waiting on ticket', 'warn'));

    const left = stackOf(
      vstack({ spacing: GAP.xs }),
      heading,
      muted(`#${i.ticketId} ${i.ticketTitle}`),
      muted(join([localDate(i.dateUtc), i.detail]))
    );
    // The server's label starts with a separator meant for sitting at the end of a line.
    const hold = i.holdLabel.replace(/^[\s•·]+/, '');
    if (!i.ready && hold) left.controls.add(muted(hold, 'warn'));

    const row = splitRow({ align: 'start', spacing: GAP.md });
    fillWith(row, left);
    row.controls.add(value(money(i.amount), { font: FONT.bold }));
    const shell = card();
    shell.controls.add(row);
    shell.onActivate(() => ctx.go(`/tickets/${i.ticketId}`));
    return shell;
  }

  function showAutoBilling(a: AutoBillingDto): void {
    enabled.checked = a.enabled;
    day.text = String(a.dayOfMonth);
    const who = a.adminRecipients.length > 0
      ? `Administrators emailed: ${a.adminRecipients.join(', ')}.`
      : 'No administrator has an email address, so nobody will be told.';
    scheduleLine.text = `${autoBillLine(a.enabled, a.dayOfMonth, a.nextRunUtc)} ${who}`;
    scheduleLine.role = a.adminRecipients.length > 0 ? 'muted' : 'warn';
    lastLine.text = a.lastRunUtc ? `Last run ${localDateTime(a.lastRunUtc)}: ${a.lastResult}` : '';
    lastLine.visible = !!a.lastRunUtc;
    relayout(autoCard);
  }

  async function refresh(): Promise<void> {
    try {
      const [entries, purchases, config, auto] = await Promise.all([
        crm.getTimeEntries(customerId, ctx.dispose.signal()),
        crm.getPurchases(customerId, ctx.dispose.signal()),
        crm.getBillingConfig(customerId, ctx.dispose.signal()),
        crm.getAutoBilling(customerId, ctx.dispose.signal())
      ]);
      if (ctx.cancelled()) return;
      const items = uninvoicedItems(entries, purchases, config.purchaseMarkupPercent);
      const t = uninvoicedTotals(items);
      list.set(items);
      empty.update(items.length);
      summary.text = items.length === 0
        ? ''
        : join([
            t.readyCount > 0 ? `${money(t.ready)} ready to invoice (${plural(t.readyCount, 'item')})` : 'Nothing ready to invoice yet',
            t.heldCount > 0 ? `${money(t.held)} waiting on unfinished tickets (${plural(t.heldCount, 'item')})` : ''
          ]);
      summary.visible = items.length > 0;
      generate.enabled = t.readyCount > 0;
      showAutoBilling(auto);
      relayout(panel);
    } catch (error) {
      if (!ctx.cancelled()) status.error(`Could not load uninvoiced work: ${describeError(error)}`);
    }
  }

  async function generateNow(): Promise<void> {
    status.busy('Generating…');
    try {
      const invoice = await crm.generateInvoice(customerId);
      if (ctx.cancelled()) return;
      status.ok(`Generated ${invoice.number} for ${money(invoice.total)} as a draft.`);
      options.onInvoiced();
      await refresh();
    } catch (error) {
      if (!ctx.cancelled()) status.error(describeError(error));
    }
  }

  function readDay(): number | null {
    const n = Number(day.text.trim());
    return Number.isInteger(n) && n >= AutoBillingLimits.MinDay && n <= AutoBillingLimits.MaxDay ? n : null;
  }

  async function saveAutoBilling(): Promise<void> {
    const d = readDay();
    if (d === null) {
      autoStatus.error(`Enter a day from ${AutoBillingLimits.MinDay} to ${AutoBillingLimits.MaxDay}.`);
      return;
    }
    autoStatus.busy('Saving…');
    try {
      const saved = await crm.updateAutoBilling(customerId, enabled.checked, d);
      if (ctx.cancelled()) return;
      showAutoBilling(saved);
      autoStatus.ok(saved.enabled ? `Saved. ${autoBillLine(true, saved.dayOfMonth, saved.nextRunUtc)}` : 'Saved. Auto-billing is off.');
    } catch (error) {
      if (!ctx.cancelled()) autoStatus.error(describeError(error));
    }
  }

  async function runAutoBillingNow(): Promise<void> {
    const ok = await confirm(ctx.desktop, {
      title: 'Run auto-billing now',
      message:
        'Invoice the work on resolved and closed tickets now? If there is any, the invoice is issued and emailed ' +
        'to the billing contact. The administrators are emailed either way. This month\'s scheduled run still happens.',
      okLabel: 'ok'
    });
    if (!ok || ctx.cancelled()) return;
    autoStatus.busy('Running…');
    try {
      const result = await crm.runAutoBilling(customerId);
      if (ctx.cancelled()) return;
      if (result.invoice) autoStatus.ok(result.message);
      else autoStatus.set(result.message);
      options.onInvoiced();
      await refresh();
    } catch (error) {
      if (!ctx.cancelled()) autoStatus.error(describeError(error));
    }
  }

  return { panel, refresh };
}
